/** Presentation of the three weakest positions widget (PV10, SE-8337). */
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Switch } from "@/components/atoms/Switch";
import { Text } from "@/components/atoms/Text";
import WatchlistThreeWeakestRow from "@/features/scouting/components/molecules/WatchlistThreeWeakestRow";
import type { WeakSlotTargetPool } from "@/features/scouting/hooks/useWeakSlotTargetCounts";
import type { WeakestPositionRow } from "@/features/scouting/hooks/useThreeWeakestPositions";
import type { UnassessableSlot } from "@/features/scouting/utils/threeWeakest";
import type { TacticalSlot } from "@/generated/graphql";

/** Prepared props for the widget presentation. */
export interface WatchlistThreeWeakestViewProps {
  readonly rows: readonly WeakestPositionRow[];
  readonly unassessable: readonly UnassessableSlot[];
  /** True until the roster has loaded. */
  readonly loading: boolean;
  /** Roster read failure, shown with a retry ahead of the empty state. */
  readonly error: string | null;
  readonly collapsed: boolean;
  readonly pool: WeakSlotTargetPool;
  /** The All-watchlists switch exists in watchlist mode only. */
  readonly showPoolSwitch: boolean;
  /** Re-reads the roster after a failure. */
  readonly onRetry: () => void;
  readonly onToggleCollapsed: () => void;
  /** Switch value: true = all watchlists, false = this list. */
  readonly onPoolValueChange: (allWatchlists: boolean) => void;
  readonly onViewTargets: (slot: TacticalSlot) => void;
  /**
   * The surface's Add-player entry (SE-8414): the empty-slot row at zero
   * routes here where present; absent, it stays "View 0 targets", disabled.
   */
  readonly onAddPlayer?: (slot: TacticalSlot) => void;
}

/**
 * Renders the three weakest positions of the current squad with the pool
 * switch, the collapse toggle and the View N targets hand-off per row.
 * Layout follows the "Block · 3 weakest positions" frame (381:4529): header,
 * rows, the all-watchlists switch row, then the static caption.
 * @param props - Prepared rows, pool, collapse state and callbacks.
 * @param props.rows - Weakness rows in order, at most three.
 * @param props.unassessable - Slots covered only by unrated players.
 * @param props.loading - True until the roster has loaded.
 * @param props.error - Roster read failure, when one happened.
 * @param props.collapsed - Whether only the header shows.
 * @param props.pool - Pool the counts come from.
 * @param props.showPoolSwitch - Whether the All-watchlists switch renders.
 * @param props.onRetry - Re-reads the roster after a failure.
 * @param props.onToggleCollapsed - Toggles the collapsed state.
 * @param props.onPoolValueChange - Receives the switch value.
 * @param props.onViewTargets - Opens one slot's targets.
 * @param props.onAddPlayer - Opens the add-player flow, when the surface has one.
 */
const WatchlistThreeWeakestView = ({
  rows,
  unassessable,
  loading,
  error,
  collapsed,
  pool,
  showPoolSwitch,
  onRetry,
  onToggleCollapsed,
  onPoolValueChange,
  onViewTargets,
  onAddPlayer,
}: WatchlistThreeWeakestViewProps) => (
  // SE-8486 (W25–W27): px 24 · py 16 as the frame draws the block; the
  // header owns the 8 px under the title, the rows own their dividers, and
  // the tail (switch row + note) owns the 12 px above itself — no gap on the
  // block, so the header sits exactly 16 px from the top.
  <Stack
    testID="watchlist-three-weakest"
    className="border-b border-outline-default px-6 py-4"
  >
    <Stack
      testID="watchlist-three-weakest-header"
      direction="horizontal"
      className="items-center justify-between gap-2 pb-2"
    >
      <Text
        variant="micro-strong"
        className="tracking-formation-label text-content-muted"
      >
        3 WEAKEST POSITIONS
      </Text>
      <Button
        testID="watchlist-three-weakest-collapse"
        accessibilityLabel={
          collapsed
            ? "Expand three weakest positions"
            : "Collapse three weakest positions"
        }
        icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
        iconOnly
        // Bare chevron (969:19077): `tone="neutral"` keeps its muted fill on
        // the link variant, which drew a grey slab around the icon (SE-8442).
        variant="link"
        iconTone="muted"
        size="xs"
        // SE-8486 (W26): a text-like glyph, not the xs button's 28×32 box —
        // that box was what pushed the title 11 px down from the frame.
        className="h-auto p-0"
        onPress={onToggleCollapsed}
      />
    </Stack>
    {collapsed ? null : (
      <Stack>
        {loading ? (
          <Text variant="caption" className="text-content-muted">
            Reading the current squad…
          </Text>
        ) : null}
        {error ? (
          <Stack direction="horizontal" className="items-center gap-2">
            <Text
              testID="watchlist-three-weakest-error"
              variant="caption"
              className="text-status-error"
            >
              {error}
            </Text>
            <Button
              testID="watchlist-three-weakest-retry"
              label="Retry"
              variant="outline"
              tone="neutral"
              size="xs"
              onPress={onRetry}
            />
          </Stack>
        ) : null}
        {!loading && !error && rows.length === 0 ? (
          <Text variant="caption" className="text-content-muted">
            No weakness to show.
          </Text>
        ) : null}
        {rows.map(row => (
          <WatchlistThreeWeakestRow
            key={row.slot}
            row={row}
            onViewTargets={onViewTargets}
            onAddPlayer={onAddPlayer}
          />
        ))}
        {unassessable.length > 0 ? (
          <Text
            testID="watchlist-three-weakest-unassessable"
            variant="micro"
            className="text-content-muted"
          >
            {unassessable
              .map(
                item => `${item.slot} not assessable · ${item.unrated} unrated`
              )
              .join("\n")}
          </Text>
        ) : null}
        <Stack className="gap-2 pt-3">
          {showPoolSwitch ? (
            <Stack
              testID="watchlist-three-weakest-pool-row"
              direction="horizontal"
              className="items-center gap-3"
            >
              {/* SE-8486 (W31): the switch leads the row at its natural size.
                  The vendored `sm` is a 0.75 transform, which keeps the 40×20
                  layout box while painting 30×15 — the 43 px gap the audit
                  measured was the empty part of that box plus the gap. */}
              <Switch
                testID="watchlist-three-weakest-pool-switch"
                accessibilityLabel="Count targets across all watchlists"
                size="md"
                value={pool === "all-watchlists"}
                onValueChange={onPoolValueChange}
              />
              <Text variant="body-strong" className="text-content-primary">
                Targets from all watchlists
              </Text>
            </Stack>
          ) : null}
          <Text variant="caption" className="text-content-subtle">
            Current squad, static · GPR. Ranked by worst starter, then biggest
            gap to the second choice.
          </Text>
        </Stack>
      </Stack>
    )}
  </Stack>
);

WatchlistThreeWeakestView.displayName = "WatchlistThreeWeakestView";
export default memo(WatchlistThreeWeakestView);
